import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { Key, ReactElement, useState } from 'react'

import {
   Button,
   Hierarchy,
   Item,
   Picker,
   SubVariant,
   TextField
} from '@flowwai-frontend/design-system'
import {
   environmentsEndPointsURLs,
   getGraphQLBaseURL,
   getRestBaseURL,
   getSelectedEnv,
   setGraphQLBaseURL,
   setLoginClientId,
   getLoginClientId,
   setRestBaseURL,
   setSelectedEnv
} from '@flowwai-frontend/bps-officer-web/env-config'
import { useCustomT } from '@flowwai-frontend/shared/common-components'

import { EnvironmentSelectorEnum } from '../../constants/loginConstants'
import { LOGIN_ROUTE_PATH } from '../../constants/navigationConstants'

import {
   environmentClassName,
   textFieldContainerClassName,
   updateButtonClassName
} from './styles'

interface EnvironmentsEndPointsType {
   loginClientId: string
   URLs: {
      restBaseURL: string
      graphqlBaseURL: string
   }
}

interface Props {
   containerClassName?: string
}

const EnvironmentSelector = (props: Props): React.ReactElement => {
   const t = useCustomT('auth:login')

   const selectedEnv = getSelectedEnv()
   const storedLoginClientId = getLoginClientId()

   const storedGraphqlBaseURL = getGraphQLBaseURL()
   const storedRestBaseURL = getRestBaseURL()

   const enableCustomTextFields = selectedEnv === EnvironmentSelectorEnum.Others

   const [customRestBaseURL, setCustomRestBaseURL] = useState<string>(
      storedRestBaseURL || ''
   )
   const [customGraphqlBaseURL, setCustomGraphqlBaseURL] = useState<string>(
      storedGraphqlBaseURL || ''
   )

   const [showCustomTextField, setShowCustomTextField] = useState<boolean>(
      enableCustomTextFields
   )

   const isEnableUpdate =
      customRestBaseURL !== '' && customGraphqlBaseURL !== ''

   const options = [
      {
         id: EnvironmentSelectorEnum.Alpha,
         name: EnvironmentSelectorEnum.Alpha
      },
      { id: EnvironmentSelectorEnum.Beta, name: EnvironmentSelectorEnum.Beta },
      {
         id: EnvironmentSelectorEnum.Gamma,
         name: EnvironmentSelectorEnum.Gamma
      },
      {
         id: EnvironmentSelectorEnum.Others,
         name: EnvironmentSelectorEnum.Others
      }
   ]

   const onClickUpdate = (event: React.SyntheticEvent): void => {
      event.preventDefault()

      setSelectedEnv(EnvironmentSelectorEnum.Others)
      const customURLs = {
         loginClientId: storedLoginClientId || '',
         URLs: {
            restBaseURL: customRestBaseURL,
            graphqlBaseURL: customGraphqlBaseURL
         }
      }

      setEnvironmentURLs(customURLs)

      window.location.href = LOGIN_ROUTE_PATH
   }

   const getSelectedEnvironment = (value: string) =>
      environmentsEndPointsURLs.find(
         eachEnv => eachEnv.envName === value.toLowerCase()
      )

   const setEnvironmentURLs = (
      selectedEnvironment?: EnvironmentsEndPointsType
   ): void => {
      if (!selectedEnvironment) return

      setLoginClientId(selectedEnvironment.loginClientId)

      setGraphQLBaseURL(selectedEnvironment.URLs.graphqlBaseURL)
      setRestBaseURL(selectedEnvironment.URLs.restBaseURL)
   }

   const onSelectionChange = (key: Key): void => {
      if (EnvironmentSelectorEnum.Others === key) {
         setShowCustomTextField(true)
         return
      }

      setSelectedEnv(key as string)
      setShowCustomTextField(false)
      const selectedEnvironment = getSelectedEnvironment(key as string)
      setEnvironmentURLs(selectedEnvironment)

      window.location.href = LOGIN_ROUTE_PATH
   }

   const renderCustomTextFields = (): ReactElement => (
      <form onSubmit={onClickUpdate}>
         <div className={textFieldContainerClassName}>
            <TextField
               label={t('restBaseURL')}
               containerClassName='w-4/4'
               title={customRestBaseURL}
               value={customRestBaseURL}
               size={'ExtraSmall'}
               placeholder={t('enterRestBaseURL')}
               onChange={(value: string) => setCustomRestBaseURL(value)}
            />
         </div>
         <div className={textFieldContainerClassName}>
            <TextField
               label={t('graphqlBaseURL')}
               containerClassName='w-4/4'
               title={customGraphqlBaseURL}
               value={customGraphqlBaseURL}
               size={'ExtraSmall'}
               placeholder={t('enterGraphqlBaseURL')}
               onChange={(value: string) => setCustomGraphqlBaseURL(value)}
            />
         </div>
         <div className={updateButtonClassName}>
            <Button
               type='submit'
               hierarchy={Hierarchy.Primary}
               subVariant={SubVariant.Primary}
               size={'Small'}
               className='mt-16px'
               isDisabled={!isEnableUpdate}
            >
               {t('update')}
            </Button>
         </div>
      </form>
   )

   return (
      <div className={cn(environmentClassName, props.containerClassName)}>
         <div>
            <Picker
               label={t('environment')}
               items={options}
               placeholder={t('selectAnEnvironment')}
               defaultSelectedKey={selectedEnv || EnvironmentSelectorEnum.Alpha}
               onSelectionChange={onSelectionChange}
               size={'ExtraSmall'}
            >
               {item => <Item key={item.id}>{item.name}</Item>}
            </Picker>
         </div>
         {showCustomTextField && renderCustomTextFields()}
      </div>
   )
}

export default observer(EnvironmentSelector)
